import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import {
  Phone, Mail, MapPin, Clock, ArrowUpRight, ArrowLeft, Check, Hammer,
} from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Section, SectionHeader } from "@/components/Section";
import { COMPANY, SERVICES } from "@/lib/site";
import { submitContact } from "@/lib/api";

const TIMELINE_OPTIONS = [
  { value: "asap", label: "ASAP" },
  { value: "1-3-months", label: "1–3 months" },
  { value: "3-6-months", label: "3–6 months" },
  { value: "planning", label: "Just planning" },
];

const BUDGET_OPTIONS = [
  { value: "<10k", label: "Under $10k" },
  { value: "10-25k", label: "$10k – $25k" },
  { value: "25-50k", label: "$25k – $50k" },
  { value: "50-100k", label: "$50k – $100k" },
  { value: "100k+", label: "$100k+" },
  { value: "not-sure", label: "Not sure yet" },
];

const STEP_LABELS = ["Service", "Project", "Your info"];

export default function Contact() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Wizard state
  const [service, setService] = useState("");
  const [project, setProject] = useState({
    timeline: "",
    budget: "",
    details: "",
  });
  const [info, setInfo] = useState({ name: "", email: "", phone: "" });

  const total = STEP_LABELS.length;

  const next = () => {
    if (step === 1 && !service) {
      toast.error("Pick a service to continue.");
      return;
    }
    if (step === 2 && project.details.trim().length < 5) {
      toast.error("Add a few words about your project.");
      return;
    }
    setStep((s) => Math.min(total, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!info.name || !info.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const messageParts = [
        `Service: ${service}`,
        project.timeline && `Timeline: ${TIMELINE_OPTIONS.find(t => t.value === project.timeline)?.label || project.timeline}`,
        project.budget && `Budget: ${BUDGET_OPTIONS.find(b => b.value === project.budget)?.label || project.budget}`,
      ].filter(Boolean);

      const message = `${messageParts.join("\n")}\n\nProject details:\n${project.details}`;

      await submitContact({
        name: info.name,
        email: info.email,
        phone: info.phone || null,
        service,
        message,
      });
      toast.success("Thanks! We'll be in touch within 1 business day.");
      // reset wizard
      setStep(1);
      setService("");
      setProject({ timeline: "", budget: "", details: "" });
      setInfo({ name: "", email: "", phone: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact & Free Quote"
        description="Request a free quote from IVrenovation. Renovation and construction services across the Durham Region and the GTA."
      />
      <Section>
        <div className="grid grid-cols-12 gap-10">
          {/* LEFT SIDEBAR */}
          <div className="col-span-12 lg:col-span-5">
            <SectionHeader
              overline="Get a free quote"
              title="Tell us about your project."
              description="Three quick steps. We'll come back within one business day with next steps, a site visit and a transparent quote."
            />

            <div className="mt-10 flex flex-col gap-5 text-zinc-700">
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-phone"
              >
                <span className="w-10 h-10 bg-graphite text-white flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-zinc-500">Phone / WhatsApp</span>
                  <span className="font-bold text-zinc-900 group-hover:text-orange-600 transition">{COMPANY.phone}</span>
                </span>
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-start gap-4 group"
                data-testid="contact-email"
              >
                <span className="w-10 h-10 bg-graphite text-white flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-zinc-500">Email</span>
                  <span className="font-bold text-zinc-900 group-hover:text-orange-600 transition">{COMPANY.email}</span>
                </span>
              </a>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 bg-graphite text-white flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-zinc-500">Service Area</span>
                  <span className="font-bold text-zinc-900">{COMPANY.address}</span>
                </span>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 bg-graphite text-white flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-zinc-500">Hours</span>
                  <span className="font-bold text-zinc-900">{COMPANY.hours}</span>
                </span>
              </div>
            </div>
          </div>

          {/* WIZARD */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="col-span-12 lg:col-span-7 border border-zinc-200 bg-white p-8 md:p-10"
            data-testid="contact-form"
          >
            {/* Progress */}
            <div className="flex items-center gap-3 mb-8" data-testid="wizard-progress">
              {STEP_LABELS.map((label, i) => {
                const n = i + 1;
                const active = n === step;
                const complete = n < step;
                return (
                  <div key={label} className="flex items-center gap-3 flex-1">
                    <div
                      className={`w-8 h-8 flex items-center justify-center text-xs font-bold border ${
                        active
                          ? "bg-orange-600 text-white border-orange-600"
                          : complete
                          ? "bg-graphite text-white border-graphite"
                          : "bg-white text-zinc-400 border-zinc-300"
                      }`}
                      data-testid={`wizard-step-${n}`}
                    >
                      {complete ? <Check className="w-4 h-4" /> : String(n).padStart(2, "0")}
                    </div>
                    <span
                      className={`text-xs uppercase tracking-wider font-bold whitespace-nowrap ${
                        active ? "text-zinc-900" : complete ? "text-zinc-700" : "text-zinc-400"
                      }`}
                    >
                      {label}
                    </span>
                    {n !== total && (
                      <div className={`flex-1 h-px ${complete ? "bg-graphite" : "bg-zinc-200"}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Steps */}
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25 }}
                  data-testid="wizard-step-1-panel"
                >
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight text-zinc-950">
                    What can we build for you?
                  </h3>
                  <p className="mt-2 text-sm text-zinc-600">Pick the main service. You can add details next.</p>

                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICES.map((s) => {
                      const Icon = Icons[s.icon] || Hammer;
                      const selected = service === s.title;
                      return (
                        <button
                          key={s.slug}
                          type="button"
                          onClick={() => setService(s.title)}
                          data-testid={`wizard-service-${s.slug}`}
                          className={`text-left border p-4 transition group ${
                            selected
                              ? "border-orange-600 bg-orange-50"
                              : "border-zinc-300 bg-white hover:border-zinc-900"
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${selected ? "text-orange-600" : "text-zinc-700 group-hover:text-zinc-900"}`} />
                          <p className={`mt-3 font-bold text-sm leading-tight ${selected ? "text-orange-700" : "text-zinc-900"}`}>
                            {s.title}
                          </p>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setService("Multi-trade / Other")}
                      data-testid="wizard-service-other"
                      className={`text-left border p-4 transition ${
                        service === "Multi-trade / Other"
                          ? "border-orange-600 bg-orange-50"
                          : "border-zinc-300 bg-white hover:border-zinc-900"
                      }`}
                    >
                      <Hammer className="w-5 h-5 text-zinc-700" />
                      <p className="mt-3 font-bold text-sm leading-tight text-zinc-900">Multi-trade / Other</p>
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25 }}
                  data-testid="wizard-step-2-panel"
                >
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight text-zinc-950">
                    Tell us a bit more.
                  </h3>
                  <p className="mt-2 text-sm text-zinc-600">Helps us prepare a useful first response.</p>

                  <div className="mt-6 space-y-6">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Timeline</label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {TIMELINE_OPTIONS.map((t) => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => setProject((p) => ({ ...p, timeline: t.value }))}
                            data-testid={`wizard-timeline-${t.value}`}
                            className={`px-4 py-2 text-sm border transition ${
                              project.timeline === t.value
                                ? "bg-graphite text-white border-graphite"
                                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                            }`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Estimated budget</label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {BUDGET_OPTIONS.map((b) => (
                          <button
                            key={b.value}
                            type="button"
                            onClick={() => setProject((p) => ({ ...p, budget: b.value }))}
                            data-testid={`wizard-budget-${b.value}`}
                            className={`px-4 py-2 text-sm border transition ${
                              project.budget === b.value
                                ? "bg-graphite text-white border-graphite"
                                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                            }`}
                          >
                            {b.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="details" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Project details *</label>
                      <textarea
                        id="details"
                        rows={5}
                        required
                        value={project.details}
                        onChange={(e) => setProject((p) => ({ ...p, details: e.target.value }))}
                        placeholder="Scope, rooms, square footage, any inspiration links."
                        data-testid="wizard-details"
                        className="border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:outline-none focus:border-orange-600 transition resize-none"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25 }}
                  data-testid="wizard-step-3-panel"
                >
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight text-zinc-950">
                    Where can we reach you?
                  </h3>
                  <p className="mt-2 text-sm text-zinc-600">We&apos;ll respond within one business day.</p>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Name *</label>
                      <input
                        id="name"
                        type="text"
                        required
                        value={info.name}
                        onChange={(e) => setInfo((i) => ({ ...i, name: e.target.value }))}
                        data-testid="wizard-input-name"
                        className="border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:outline-none focus:border-orange-600 transition"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Email *</label>
                      <input
                        id="email"
                        type="email"
                        required
                        value={info.email}
                        onChange={(e) => setInfo((i) => ({ ...i, email: e.target.value }))}
                        data-testid="wizard-input-email"
                        className="border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:outline-none focus:border-orange-600 transition"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Phone</label>
                      <input
                        id="phone"
                        type="tel"
                        value={info.phone}
                        onChange={(e) => setInfo((i) => ({ ...i, phone: e.target.value }))}
                        data-testid="wizard-input-phone"
                        className="border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:outline-none focus:border-orange-600 transition"
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-7 border border-zinc-200 bg-zinc-50 p-5 text-sm">
                    <p className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-3">Summary</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <p><span className="text-zinc-500">Service:</span> <span className="font-bold text-zinc-900">{service || "—"}</span></p>
                      <p><span className="text-zinc-500">Timeline:</span> <span className="font-bold text-zinc-900">{TIMELINE_OPTIONS.find(t => t.value === project.timeline)?.label || "—"}</span></p>
                      <p><span className="text-zinc-500">Budget:</span> <span className="font-bold text-zinc-900">{BUDGET_OPTIONS.find(b => b.value === project.budget)?.label || "—"}</span></p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="mt-10 flex items-center justify-between gap-4">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={back}
                  data-testid="wizard-back"
                  className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-700 hover:text-orange-600 transition"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              ) : (
                <span />
              )}

              {step < total ? (
                <button
                  type="button"
                  onClick={next}
                  data-testid="wizard-next"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-7 py-3.5 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 transition"
                >
                  Continue <ArrowUpRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="wizard-submit"
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-400 text-white px-7 py-3.5 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 transition"
                >
                  {loading ? "Sending…" : "Send request"}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              )}
            </div>

            <p className="text-xs text-zinc-500 mt-6">
              By submitting, you agree we may contact you about your renovation project.
            </p>
          </motion.form>
        </div>
      </Section>
    </div>
  );
}
